import React, { useEffect, useMemo, useState } from "react";
import { Building2, Search, Plus, Lock, MapPin, User, ShieldCheck, ArrowUpRight, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import ClientModal from "@/components/clients/ClientModal";
import { endpoints, getTenantScope } from "@/lib/api";

const STATUS_TONE = {
  "Certified": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "In Progress": "bg-amber-50 text-amber-700 border-amber-200",
  "Pending Renewal": "bg-blue-50 text-blue-700 border-blue-200",
  "Lapsed": "bg-rose-50 text-rose-700 border-rose-200",
  "Not Certified": "bg-slate-100 text-slate-600 border-slate-200",
};

const initials = (name) => (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

export default function ClientsPage() {
  const [user, setUser] = useState({ name: "" });
  const [authorized, setAuthorized] = useState(null);
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [consultantFilter, setConsultantFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [scopeError, setScopeError] = useState(false);
  const navigate = useNavigate();

  const load = () => endpoints.listClients()
    .then((d) => { setScopeError(false); setClients(d); })
    .catch((e) => { if (e.response?.status === 403) setScopeError(true); });

  useEffect(() => {
    endpoints.me().then((u) => {
      setUser(u);
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(u.canonical_role === "admin" || u.canonical_role === "staff" || !!me?.is_manager);
      });
    });
  }, []);

  useEffect(() => { if (authorized) load(); }, [authorized]);

  const consultants = useMemo(() => Array.from(new Set(
    clients.flatMap((c) => (c.assigned_consultants && c.assigned_consultants.length) ? c.assigned_consultants : (c.assigned_consultant ? [c.assigned_consultant] : []))
  )).sort(), [clients]);

  const filtered = useMemo(() => clients.filter((c) => {
    if (q && !`${c.company_name} ${c.industry} ${c.primary_email}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (status !== "all" && c.certification_status !== status) return false;
    if (consultantFilter !== "all") {
      const list = (c.assigned_consultants && c.assigned_consultants.length) ? c.assigned_consultants : (c.assigned_consultant ? [c.assigned_consultant] : []);
      if (!list.includes(consultantFilter)) return false;
    }
    return true;
  }), [clients, q, status, consultantFilter]);

  if (authorized === null) return <div className="p-8 text-sm text-slate-500">Loading...</div>;

  if (scopeError || getTenantScope()?.role === "client") {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mb-4">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="font-display text-xl font-extrabold mb-1">Directory hidden</h1>
          <p className="text-sm text-slate-500">You are viewing the portal as a client user. The full ISOQuest directory is only available to staff. Use the amber banner above to exit the simulator.</p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="p-6 lg:p-10 max-w-3xl mx-auto">
        <div className="glass-card rounded-3xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-4">
            <Lock className="h-5 w-5" />
          </div>
          <h1 className="font-display text-xl font-extrabold mb-1">Authorized staff only</h1>
          <p className="text-sm text-slate-500">The client directory is restricted to ISOQuest staff.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="glass-card rounded-3xl p-4 sm:p-5 mb-5 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-lg sm:text-xl font-extrabold tracking-tight">Client Directory</h1>
            <p className="text-xs text-slate-500">{filtered.length} of {clients.length} clients</p>
          </div>
          <Badge variant="outline" className="ml-2 gap-1 text-[10px] rounded-full bg-indigo-50 text-indigo-700 border-indigo-200">
            <ShieldCheck className="h-2.5 w-2.5" /> Staff only
          </Badge>
        </div>
        <Button data-testid="clients-new-button" className="brand-gradient text-white ml-auto" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> New client
        </Button>
      </div>

      <div className="glass-card rounded-3xl p-4 mb-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="clients-search-input" placeholder="Search company, industry, email..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-9" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="clients-status-filter" className="h-9 w-[180px] text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="Certified">Certified</SelectItem>
            <SelectItem value="In Progress">In Progress</SelectItem>
            <SelectItem value="Pending Renewal">Pending Renewal</SelectItem>
            <SelectItem value="Lapsed">Lapsed</SelectItem>
            <SelectItem value="Not Certified">Not Certified</SelectItem>
          </SelectContent>
        </Select>
        <Select value={consultantFilter} onValueChange={setConsultantFilter}>
          <SelectTrigger data-testid="clients-consultant-filter" className="h-9 w-[180px] text-xs"><SelectValue placeholder="Consultant" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All consultants</SelectItem>
            {consultants.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="glass-card rounded-3xl p-10 text-center text-sm text-slate-500">No clients match your search.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <button
              key={c.slug}
              data-testid={`client-card-${c.slug}`}
              onClick={() => navigate(`/clients/${c.slug}`)}
              className="glass-card rounded-3xl p-5 text-left card-hover group"
            >
              <div className="flex items-start gap-3">
                {c.logo_url ? (
                  <img src={c.logo_url} alt={c.company_name} className="h-12 w-12 rounded-2xl object-cover ring-2 ring-indigo-500/20" />
                ) : (
                  <div className="h-12 w-12 rounded-2xl brand-gradient text-white font-bold flex items-center justify-center shadow-md shadow-indigo-500/25">
                    {initials(c.company_name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm truncate">{c.company_name}</h3>
                    <ArrowUpRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-indigo-600 transition-colors" />
                  </div>
                  <p className="text-[11px] text-slate-500 truncate">{c.industry || "—"}</p>
                </div>
                <Badge variant="outline" className={`text-[10px] rounded-full shrink-0 ${STATUS_TONE[c.certification_status] || ""}`}>
                  {c.certification_status}
                </Badge>
              </div>

              <div className="flex flex-wrap gap-1 mt-3">
                {(c.iso_standards || []).slice(0, 3).map((s) => (
                  <span key={s.standard} className="inline-flex items-center gap-1 rounded-full border border-indigo-200 dark:border-indigo-500/25 bg-indigo-50/70 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 text-[10px] font-semibold">
                    <BadgeCheck className="h-2.5 w-2.5" /> {s.standard}
                  </span>
                ))}
              </div>

              <div className="mt-3 space-y-1 text-[11px] text-slate-500">
                {c.address?.city && (
                  <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {c.address.city}{c.address.country ? `, ${c.address.country}` : ""}</div>
                )}
                {(() => {
                  const list = (c.assigned_consultants && c.assigned_consultants.length) ? c.assigned_consultants : (c.assigned_consultant ? [c.assigned_consultant] : []);
                  if (!list.length) return null;
                  const extra = list.length - 1;
                  return (
                    <div className="flex items-center gap-1.5" title={list.join(", ")}>
                      <User className="h-3 w-3" /> {list[0]}{extra > 0 && <span className="text-slate-400"> +{extra}</span>}
                    </div>
                  );
                })()}
              </div>
            </button>
          ))}
        </div>
      )}

      <ClientModal
        open={modalOpen}
        initial={null}
        onOpenChange={setModalOpen}
        onChanged={() => { load(); setModalOpen(false); }}
      />
    </div>
  );
}
